import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot
} from 'recharts';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function formatMonthYear(month, year) {
  return `${MONTH_NAMES[month]} ${year}`;
}

function CustomLabel({ viewBox, value }) {
  const { x, y } = viewBox;
  return (
    <text
      x={x}
      y={y - 12}
      fill="#6C18C9"
      fontSize={12}
      fontWeight={700}
      textAnchor="middle"
    >
      {Math.round(value)}
    </text>
  );
}

export default function CScoreChart({ data, title = 'Average CScore', onPointClick, selectedMonth, selectedYear }) {
  const chartData = data.map(d => ({
    ...d,
    label: formatMonthYear(d.month, d.year),
    shortLabel: `${MONTH_NAMES[d.month]?.slice(0, 3) || ''} ${d.year}`,
    displayLabel: `${MONTH_NAMES[d.month]?.slice(0, 3) || ''}\n${d.year}`,
    fullLabel: `${MONTH_NAMES[d.month]} ${d.year}`,
  }));

  const scores = chartData.map(d => d.score);
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  const padding = Math.max(10, Math.ceil((maxScore - minScore) * 0.15));
  const yMin = Math.floor((minScore - padding) / 10) * 10;
  const yMax = Math.ceil((maxScore + padding) / 10) * 10;

  return (
    <div className="bg-white rounded-xl border border-[#E6E6E6] p-6">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-lg font-bold text-text">{title}</h3>
        <div className="relative group">
          <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center cursor-help">
            <span className="text-gray-400 text-xs">?</span>
          </div>
          <div className="absolute left-6 top-0 z-50 hidden group-hover:block bg-white rounded-xl shadow-lg border border-[#E6E6E6] p-5 w-[260px]">
            <h4 className="text-base font-bold text-text mb-3">CScore Key</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#1E6C30' }} />
                <span className="text-sm font-semibold text-text w-[100px]">EXCELLENT</span>
                <span className="text-sm text-gray-600">800-850</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#4d7c0f' }} />
                <span className="text-sm font-semibold text-text w-[100px]">VERY GOOD</span>
                <span className="text-sm text-gray-600">740 - 799</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#DCA114' }} />
                <span className="text-sm font-semibold text-text w-[100px]">GOOD</span>
                <span className="text-sm text-gray-600">670 - 739</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#e67e22' }} />
                <span className="text-sm font-semibold text-text w-[100px]">FAIR</span>
                <span className="text-sm text-gray-600">580 - 669</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#B0200C' }} />
                <span className="text-sm font-semibold text-text w-[100px]">POOR</span>
                <span className="text-sm text-gray-600">0 - 579</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 25, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="6 6" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="displayLabel"
            interval={0}
            tick={({ x, y, payload }) => {
              const [month, year] = (payload.value || '').split('\n');
              return (
                <g transform={`translate(${x},${y})`}>
                  <text x={0} y={0} dy={12} textAnchor="middle" fill="#9ca3af" fontSize={11}>{month}</text>
                  <text x={0} y={0} dy={25} textAnchor="middle" fill="#9ca3af" fontSize={10}>{year}</text>
                </g>
              );
            }}
            height={45}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            domain={[yMin, yMax]}
            tick={{ fontSize: 12, fill: '#9ca3af' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <Tooltip
            contentStyle={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
            labelFormatter={(label) => {
              const [month, year] = (label || '').split('\n');
              return `${month} ${year}`;
            }}
            formatter={(value) => [Math.round(value), 'CScore']}
          />
          <Line
            type="monotone"
            dataKey="score"
            stroke="#6C18C9"
            strokeWidth={2.5}
            dot={(props) => {
              const { cx, cy, payload } = props;
              const isSelected = selectedMonth === payload.month && selectedYear === payload.year;
              return (
                <circle
                  key={`dot-${payload.month}-${payload.year}`}
                  cx={cx}
                  cy={cy}
                  r={isSelected ? 6 : 4}
                  fill={isSelected ? '#fff' : '#6C18C9'}
                  stroke="#6C18C9"
                  strokeWidth={isSelected ? 3 : 2}
                />
              );
            }}
            activeDot={{
              r: 6, fill: '#6C18C9', stroke: '#fff', strokeWidth: 2,
              cursor: onPointClick ? 'pointer' : 'default',
              onClick: (e, payload) => {
                if (onPointClick && payload && payload.payload) {
                  onPointClick(payload.payload.month, payload.payload.year);
                }
              },
            }}
          />
          {chartData.map((entry, index) => (
            <ReferenceDot
              key={index}
              x={entry.displayLabel}
              y={entry.score}
              r={0}
              label={<CustomLabel value={entry.score} />}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
